import { useFetchSampleOptions } from "global/hooks/useFetchOptions";
import { paginationType } from "global/Interfaces/Interfaces";
import { useDispatch, useSelector } from "react-redux";
import Breadcrumb from "components/Breadcrumb/Breadcrumb";
import Paginations from "global/Pagination/Pagination";
import {
  AiOutlineCalendar,
  AiOutlineFolderOpen,
  AiOutlinePaperClip,
} from "react-icons/ai";
import { FiArrowLeft } from "react-icons/fi";
import { filterSurveyResults } from "redux/survey/survey-results";
import SurveyResultSearch from "./table/Search";
import MultiImageUploader from "components/importImage";

interface ResponseDate {
  day: number;
  hour: string;
  minute: string;
  month: number;
  year: number;
}

interface ResponseItem {
  id: number;
  docId: string | null;
  selectedPoint: number | null;
  selectedReply: boolean | null;
  selectContent?: { id: number; text?: string; title?: string } | null;
  responseContentList?: { id: number; text?: string; title?: string }[];
  selectQuestion?: { id: number; text: string } | null;
  responseDate?: ResponseDate;
  description?: string | null;
}

interface QuestionCardProps {
  title: string;
  response: ResponseItem;
  createdDate?: string;
}

const formatResponseDate = (date?: ResponseDate) => {
  if (!date) return "-";
  return `${date.year}/${String(date.month).padStart(2, "0")}/${String(
    date.day
  ).padStart(2, "0")} - ${date.hour}:${date.minute}`;
};

const getResponseTypeLabel = (item: ResponseItem) => {
  if (item.selectedPoint !== null) return "درجه بندی";
  if (typeof item.selectedReply === "boolean") return "بله/خیر";
  if (item.docId) return "آپلود فایل";
  if (item.responseContentList && item.responseContentList.length > 0) {
    return "چند انتخابی";
  }
  if (item.selectContent?.id) return "تک انتخابی";
  if (item.description?.trim()) return "متنی";
  return "نامشخص";
};

const renderStars = (count: number) => {
  return (
    <div className="flex items-center gap-1">
      {Array.from({ length: 5 }, (_, index) => {
        const active = index < count;
        return (
          <span
            key={index}
            className={
              active ? "text-yellow-400 text-xl" : "text-gray-200 text-xl"
            }
          >
            ★
          </span>
        );
      })}
      <span className="text-xs text-gray-500 mr-2">{count} از 5</span>
    </div>
  );
};

const renderResponseValue = (item: ResponseItem) => {
  if (item.selectedPoint !== null) {
    return renderStars(item.selectedPoint);
  }

  if (typeof item.selectedReply === "boolean") {
    return (
      <span
        className={`inline-flex px-3 py-1 rounded-full text-xs font-bold ${
          item.selectedReply
            ? "bg-green-50 text-green-700"
            : "bg-red-50 text-red-600"
        }`}
      >
        {item.selectedReply ? "بله" : "خیر"}
      </span>
    );
  }

  if (item.selectContent?.id) {
    return (
      <div className="flex flex-wrap gap-2" dir="rtl">
        <span className="px-3 py-1 text-xs font-medium rounded-full bg-purple-50 text-purple-700 border border-purple-100">
          {item.selectContent.text ||
            item.selectContent.title ||
            `شناسه ${item.selectContent.id}`}
        </span>
      </div>
    );
  }

  if (item.responseContentList && item.responseContentList.length > 0) {
    return (
      <div className="flex flex-wrap gap-2" dir="rtl">
        {item.responseContentList.map((answer) => (
          <span
            key={answer.id}
            className="px-3 py-1 text-xs font-medium rounded-full bg-gray-50 border border-gray-200 text-gray-700"
          >
            {answer?.selectedContent.text ||
              answer.title ||
              `شناسه ${answer?.selectedContent.id}`}
          </span>
        ))}
      </div>
    );
  }

  if (item.description?.trim()) {
    return (
      <textarea
        readOnly
        value={item.description}
        className="w-full min-h-[110px] rounded-xl border border-gray-100 bg-gray-50 p-4 text-sm text-gray-700 outline-none resize-none"
      />
    );
  }

  if (item.docId) {
    return (
      <MultiImageUploader
        mode="single"
        currentImage={item.docId}
        preview={true}
      />
    );
  }

  return <span className="text-sm text-gray-400">پاسخی ثبت نشده است</span>;
};

const QuestionCard: React.FC<QuestionCardProps> = ({
  title,
  response,
  createdDate,
}) => {
  const responseType = getResponseTypeLabel(response);

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 hover:shadow-md transition-all duration-200 flex flex-col justify-between min-h-[150px]">
      <div>
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span className="px-3 py-1 text-[11px] font-medium rounded-full bg-[#f1f5f9] text-gray-600">
            {responseType}
          </span>
        </div>

        <h3 className="text-sm md:text-base font-bold text-gray-800 leading-relaxed mb-4 text-right">
          {title}
        </h3>

        <div className="mb-4">{renderResponseValue(response)}</div>
      </div>

      <div className="border-t border-gray-50 pt-1 flex items-center justify-between text-[11px] text-gray-400">
        <div className="flex items-center gap-3">
          {createdDate && (
            <span className="flex items-center gap-1">
              <AiOutlineCalendar className="text-gray-400" size={14} />
              <span className="text-gray-500">{createdDate}</span>
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

const SurveyResults = () => {
  const dispatch = useDispatch();

  const { dataOptions: replyTypeSelect } = useFetchSampleOptions(
    "/customer-api/question/replyTypeSelect"
  );
  const { dataOptions: questionTypeSelect } = useFetchSampleOptions(
    "/customer-api/question/questionTypeSelect"
  );
  const { dataOptions: questionAssessmentMethodSelect } = useFetchSampleOptions(
    "/customer-api/question/questionAssessmentMethodSelect"
  );

  const { surveyResultsData, fetchpost, filter } = useSelector(
    (state: any) => state.SurveyResultsSlice
  );
  const { pageNumbers } = useSelector(
    (state: paginationType) => state.paginate
  );

  return (
    <div
      className="w-full px-4 md:px-8 py-6 bg-[#f8f9fa] min-h-screen"
      dir="rtl"
    >
      <Breadcrumb curentPage="نتایج نظرسنجی" beforePage="" />

      <div className="flex justify-end mb-4">
        <button
          type="button"
          onClick={() => window.history.back()}
          className="flex items-center gap-2 px-4 py-2 text-xs md:text-sm font-semibold text-gray-600 bg-white rounded-xl border border-gray-100 hover:bg-gray-50 transition shadow-sm"
        >
          بازگشت به منو
          <FiArrowLeft size={16} />
        </button>
      </div>

      <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-4 md:p-6">
        <div className="bg-gradient-to-r from-[#fff5f8] via-[#fdf2f6] to-[#fff7fa] rounded-2xl p-4 md:p-6 mb-6 flex flex-col sm:flex-row items-center justify-between gap-4 border border-pink-50/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#fce8f0] flex items-center justify-center text-[#7a1b2b]">
              <AiOutlineFolderOpen size={22} />
            </div>
            <div>
              <h2 className="text-base md:text-lg font-extrabold text-gray-800">
                مدیریت نتایج نظرسنجی
              </h2>
            </div>
          </div>
        </div>

        <div className="mb-6 bg-gray-50/50 rounded-2xl p-4 border border-gray-100/50">
          <SurveyResultSearch
            options={{
              replyTypeSelect: replyTypeSelect?.options,
              questionTypeSelect: questionTypeSelect?.options,
              questionAssessmentMethodSelect:
                questionAssessmentMethodSelect?.options,
            }}
          />
        </div>

        {fetchpost ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <div className="w-10 h-10 border-4 border-[#7a1b2b] border-t-transparent rounded-full animate-spin"></div>
            <p className="text-xs text-gray-500 font-medium">
              در حال بارگذاری نتایج...
            </p>
          </div>
        ) : surveyResultsData?.content?.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-sm text-gray-400">
              هیچ نتیجه‌ای برای نمایش یافت نشد.
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {surveyResultsData?.content?.map((item: ResponseItem) => (
                <QuestionCard
                  key={item.id}
                  title={item.selectQuestion?.text || "بدون عنوان"}
                  response={item}
                  createdDate={formatResponseDate(item.responseDate)}
                />
              ))}
            </div>
            <Paginations pageData={surveyResultsData?.totalElements} />
          </>
        )}
      </div>
    </div>
  );
};

export default SurveyResults;
